import { useCallback, useMemo, useState } from 'react';

import { Enum } from '~/data/enums';
import { BonusProposalProps } from '~/data/proposals';
import { BonusProgramConfig } from '~/data/rewarding/types';
import { useBonusProgramConfigUtils } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/BonusProposalForm/data/useBonusProgramConfigUtils';
import { ProposalConfigSelectorProps } from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common/components/ProposalConfigSelector';

type PropsType = {
  proposal: BonusProposalProps;
  bonusProgramConfigs: BonusProgramConfig[];
  currency: string;
  bonusTypes: Enum[];
};

type UseBonusProgramConfigOutput = {
  defaultConfigType?: string;
  configSelector: ProposalConfigSelectorProps<BonusProgramConfig>;
  selectedConfig?: BonusProgramConfig;
};

export const useBonusProgramConfig = (
  props: PropsType,
): UseBonusProgramConfigOutput => {
  const { proposal, bonusProgramConfigs, currency, bonusTypes } = props;
  const { proposalData } = proposal;

  // Utils
  const {
    getNonPresentConfig,
    findConfigById,
    getDefaultConfigs,
    convertConfigToOption,
  } = useBonusProgramConfigUtils();

  const { defaultConfigOptions, defaultConfig, defaultConfigType } =
    useMemo(() => {
      const { options, selected } = getDefaultConfigs({
        proposalConfigs: bonusProgramConfigs,
        proposalConfigType: proposalData?.incentiveShortTypeFK,
        // TODO Possibly re-enable custom program resolving later
        // customResolveSelectedConfig: (config) =>
        //   config.fEmplBonusValue === proposalData?.paymentAmount,
      });

      return {
        defaultConfigOptions: options,
        defaultConfig: selected,
        defaultConfigType: selected?.fIncentiveShortType,
      };
    }, [getDefaultConfigs, bonusProgramConfigs, proposalData]);

  // Ensure the available options don't change after the form is initialized
  // to prevent synchronization issues between config selector & form data
  const [hoursTypeConfigOptions] =
    useState<BonusProgramConfig[]>(defaultConfigOptions);

  const [selectedConfig, setSelectedConfig] = useState<
    BonusProgramConfig | undefined
  >(defaultConfig);

  const onSelectedConfigChange = useCallback(
    (
      typeConfig: BonusProgramConfig | undefined,
    ): Promise<BonusProgramConfig | undefined> => {
      return new Promise((resolve, reject) => {
        const foundConfig = findConfigById(
          hoursTypeConfigOptions,
          typeConfig?.fEmplSubUId ?? '',
        );

        if (!foundConfig) {
          reject('optionNotPresent');
        } else {
          setSelectedConfig(foundConfig);
          resolve(foundConfig);
        }
      });
    },
    [hoursTypeConfigOptions, setSelectedConfig, findConfigById],
  );

  const configSelector: ProposalConfigSelectorProps<BonusProgramConfig> = {
    getNonPresentConfig,
    selectedConfig,
    onSelectedConfigChange,
    convertConfigToOption,
    options: hoursTypeConfigOptions,
    typeEnums: bonusTypes,
    currency,
  };

  return {
    defaultConfigType,
    configSelector,
    selectedConfig,
  };
};
